/**
 * useTeamAdmin — team membership + role-assignment admin data (Phase 4/5).
 * Wraps /v1/rbac/teams/{id}/members and /v1/rbac/users/{id}/roles.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { config } from '../config';

export interface RoleAssignment {
  id: string;
  userId: string;
  role: string;
  teamId?: string | null;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export const memberKeys = {
  ofTeam: (teamId: string) => ['rbac', 'members', teamId] as const,
};
export const roleKeys = {
  ofUser: (userId: string) => ['rbac', 'roles', userId] as const,
};

export function useTeamMembers(teamId: string) {
  return useQuery<string[], Error>({
    queryKey: memberKeys.ofTeam(teamId),
    enabled: !!teamId,
    queryFn: async () => {
      const res = await fetch(
        `${config.API_URL}/v1/rbac/teams/${teamId}/members`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error(`Failed to load members (${res.status})`);
      return res.json();
    },
  });
}

export function useAddMember(teamId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { userId: string }>({
    mutationFn: async ({ userId }) => {
      const res = await fetch(
        `${config.API_URL}/v1/rbac/teams/${teamId}/members`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify({ userId }) },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || `Failed to add member (${res.status})`);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: memberKeys.ofTeam(teamId) }),
  });
}

export function useRemoveMember(teamId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { userId: string }>({
    mutationFn: async ({ userId }) => {
      const res = await fetch(
        `${config.API_URL}/v1/rbac/teams/${teamId}/members/${userId}`,
        { method: 'DELETE', headers: authHeaders() },
      );
      if (!res.ok && res.status !== 204) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || `Failed to remove member (${res.status})`);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: memberKeys.ofTeam(teamId) }),
  });
}

export function useUserRoles(userId: string) {
  return useQuery<RoleAssignment[], Error>({
    queryKey: roleKeys.ofUser(userId),
    enabled: !!userId,
    queryFn: async () => {
      const res = await fetch(
        `${config.API_URL}/v1/rbac/users/${userId}/roles`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error(`Failed to load roles (${res.status})`);
      return res.json();
    },
  });
}

export function useGrantRole(userId: string) {
  const qc = useQueryClient();
  return useMutation<RoleAssignment, Error, { role: string; teamId?: string | null }>({
    mutationFn: async (body) => {
      const res = await fetch(
        `${config.API_URL}/v1/rbac/users/${userId}/roles`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || `Failed to grant role (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: roleKeys.ofUser(userId) }),
  });
}

export function useRevokeRole(userId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { assignmentId: string }>({
    mutationFn: async ({ assignmentId }) => {
      const res = await fetch(
        `${config.API_URL}/v1/rbac/roles/${assignmentId}`,
        { method: 'DELETE', headers: authHeaders() },
      );
      if (!res.ok && res.status !== 204) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || `Failed to revoke role (${res.status})`);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: roleKeys.ofUser(userId) }),
  });
}
